import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { Box } from "ui/box";
import { Checkbox } from "ui/checkbox";
import { FieldLabel } from "ui/forms";
import { Radio } from "ui/radio";
import { Select } from "ui/select";
import { Stack } from "ui/stack";
import { TextField } from "ui/text-field";
import { Typography } from "ui/typography";

import {
  DIGITAL_CHANNELS_OPTIONS,
  FORM_DATA_CAPTURE_PAGE_OPTIONS,
  MARKETING_CHANNELS_OPTIONS,
  OTHER_OPTION_VALUE,
  PARTNER_APPROVAL_OPTIONS,
  PRINT_OPTIONS,
  toOverviewDto,
} from "containers/admin/clients/client/college-profile-page/college-profile-general-details-form.helpers";
import type {
  GeneralDetailsFormValues,
  GeneralDetailsOption,
} from "containers/admin/clients/client/college-profile-page/college-profile-general-details-form.helpers";

import type { ClientOverviewDTO } from "generated/schemas";

export type CollegeProfileGeneralDetailsFormProps = {
  formId: string;
  clientName: string;
  overview: ClientOverviewDTO | undefined;
  onSubmit: (values: { name: string; overview: ClientOverviewDTO }) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

type MarketingChannelsMultiSelectField =
  | "marketingChannels.formDataCapturePage"
  | "marketingChannels.digitalChannels"
  | "marketingChannels.print";

type MarketingChannelsOtherDetailsKey = "formDataCapturePageOtherDetails" | "printOtherDetails";

const MARKETING_CHANNELS_MULTI_SELECT_FIELDS: {
  name: MarketingChannelsMultiSelectField;
  label: string;
  options: GeneralDetailsOption[];
  otherDetailsKey?: MarketingChannelsOtherDetailsKey;
}[] = [
  {
    name: "marketingChannels.formDataCapturePage",
    label: "Form data capture page",
    options: FORM_DATA_CAPTURE_PAGE_OPTIONS,
    otherDetailsKey: "formDataCapturePageOtherDetails",
  },
  {
    name: "marketingChannels.digitalChannels",
    label: "Digital channels",
    options: DIGITAL_CHANNELS_OPTIONS,
  },
  {
    name: "marketingChannels.print",
    label: "Print",
    options: PRINT_OPTIONS,
    otherDetailsKey: "printOtherDetails",
  },
];

export const CORE_TEAM_FIELDS: {
  name: keyof GeneralDetailsFormValues["coreTeam"];
  label: string;
}[] = [
  { name: "contentDirector", label: "Content director" },
  { name: "projectCoordinator", label: "Project coordinator" },
  { name: "accountManager", label: "Account manager" },
  { name: "projectManager", label: "Project manager" },
  { name: "copyWriter", label: "Copy writer" },
  { name: "campaignManager", label: "Campaign manager" },
  { name: "designer", label: "Designer" },
  { name: "directorOfClientSuccess", label: "Director of client success" },
];

const buildDefaultValues = (
  clientName: string,
  overview: ClientOverviewDTO | undefined,
): GeneralDetailsFormValues => ({
  name: clientName,
  partnerApproval: overview?.partnerApproval ?? "",
  coreTeam: {
    contentDirector: overview?.coreTeam?.contentDirector ?? "",
    projectCoordinator: overview?.coreTeam?.projectCoordinator ?? "",
    accountManager: overview?.coreTeam?.accountManager ?? "",
    projectManager: overview?.coreTeam?.projectManager ?? "",
    copyWriter: overview?.coreTeam?.copyWriter ?? "",
    campaignManager: overview?.coreTeam?.campaignManager ?? "",
    designer: overview?.coreTeam?.designer ?? "",
    directorOfClientSuccess: overview?.coreTeam?.directorOfClientSuccess ?? "",
  },
  marketingChannels: {
    channels: overview?.marketingChannels?.channels ?? [],
    formDataCapturePage: overview?.marketingChannels?.formDataCapturePage ?? [],
    digitalChannels: overview?.marketingChannels?.digitalChannels ?? [],
    print: overview?.marketingChannels?.print ?? [],
    formDataCapturePageOtherDetails:
      overview?.marketingChannels?.formDataCapturePageOtherDetails ?? "",
    printOtherDetails: overview?.marketingChannels?.printOtherDetails ?? "",
  },
});

const toOptions = (options: GeneralDetailsOption[], values: string[]): GeneralDetailsOption[] =>
  options.filter((option) => values.includes(option.value));

/**
 * Editable form for College profile's Client overview section. New section
 * (ticket #37431) moved out of the Campaign Template UI: Partner approval,
 * Marketing channels and Core team, plus the client's top-level name — the
 * Campaign field that used to live alongside them in the campaign-template
 * schema is out of scope.
 */
const CollegeProfileGeneralDetailsForm = ({
  formId,
  clientName,
  overview,
  onSubmit,
  onDirtyChange,
}: CollegeProfileGeneralDetailsFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isDirty, errors },
  } = useForm<GeneralDetailsFormValues>({
    defaultValues: buildDefaultValues(clientName, overview),
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={handleSubmit((values) =>
        onSubmit({ name: values.name.trim(), overview: toOverviewDto(values) }),
      )}
      noValidate
      sx={{ maxWidth: 600 }}>
      <Stack spacing={2.5}>
        <TextField
          label="Client name"
          errMessage={errors.name?.message}
          {...register("name", {
            validate: (value) => value.trim().length > 0 || "Client name is required.",
          })}
        />

        <Stack spacing={1.5}>
          <FieldLabel name="partnerApproval">Do we have partner approval?</FieldLabel>
          <Controller
            name="partnerApproval"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Stack spacing={1.5}>
                {PARTNER_APPROVAL_OPTIONS.map((option) => (
                  <Radio
                    key={String(option.value)}
                    label={option.label}
                    value={String(option.value)}
                    checked={value === String(option.value)}
                    name="partnerApproval"
                    onChange={onChange}
                  />
                ))}
              </Stack>
            )}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="subtitle1" component="h4" sx={{ color: "grey.900" }}>
            Marketing channels
          </Typography>

          <Controller
            name="marketingChannels.channels"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Stack spacing={1}>
                {MARKETING_CHANNELS_OPTIONS.map((option) => {
                  const optionValue = String(option.value);
                  const isChecked = value.includes(optionValue);

                  return (
                    <Checkbox
                      key={optionValue}
                      label={option.label}
                      checked={isChecked}
                      onChange={(nowChecked: boolean) => {
                        onChange(
                          nowChecked
                            ? [...value, optionValue]
                            : value.filter((selected: string) => selected !== optionValue),
                        );
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          />

          {MARKETING_CHANNELS_MULTI_SELECT_FIELDS.map((field) => {
            const { otherDetailsKey } = field;
            const isOtherSelected =
              Boolean(otherDetailsKey) && watch(field.name).includes(OTHER_OPTION_VALUE);

            return (
              <Stack key={field.name} spacing={1.5}>
                <Controller
                  name={field.name}
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Select
                      label={field.label}
                      selectName={field.label}
                      ariaLabel={field.label}
                      multiple
                      options={field.options}
                      value={toOptions(field.options, value)}
                      onChange={(selected) => onChange(selected.map((option) => String(option.value)))}
                      onBlur={onBlur}
                    />
                  )}
                />

                {otherDetailsKey && isOtherSelected && (
                  <TextField
                    label="Other details"
                    required
                    errMessage={errors.marketingChannels?.[otherDetailsKey]?.message}
                    {...register(`marketingChannels.${otherDetailsKey}`, {
                      validate: (value) =>
                        !isOtherSelected ||
                        value.trim().length > 0 ||
                        "Other details is required when “Other” is selected.",
                    })}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="subtitle1" component="h4" sx={{ color: "grey.900" }}>
            Core team
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}>
            {CORE_TEAM_FIELDS.map((field) => (
              <TextField
                key={field.name}
                label={field.label}
                {...register(`coreTeam.${field.name}`)}
              />
            ))}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CollegeProfileGeneralDetailsForm;
